/**
 * TanStack Query wiring for Orders.
 *
 * Two reads, one write hook factory. The page key starts with `orders` and the drawer key
 * with `order` plus the id, which is what lets the `/changes` poller reach both with the
 * roots in `lib/keys.ts`. Nothing in this file polls on a timer: an order moves when the
 * tablet packs it or the matcher matches it, and the change feed already says so within
 * ten seconds. A second clock here would only add requests and disagree.
 *
 * Optimism is not used anywhere in this feature. DESIGN.md section 6 forbids it for
 * anything that moves money or leaves the building, and every write on this screen does
 * one or the other. A pending state and the server's answer is the whole pattern:
 * optimism on a refund is how a customer gets two.
 */
import { useCallback } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import type { OrderRange } from '@elutide/shared';
import { qk } from '@/lib/keys';
import { ordersApi } from './api';
import { toPageQuery, type OrdersSearch } from './searchParams';
import type { OrderDrawerDetailView, OrdersPageResponseView } from './types';

/**
 * The legend is not order data.
 *
 * It used to live under ['orders', 'legend'], which meant every write on this screen
 * invalidated the ten minute cache of a list of sentences that changes when the owner
 * settles a definition, not when an order is marked paid.
 */
export const ordersLegendKey = ['legend'] as const;
export const orderDetailKey = (orderId: number) => ['order', String(orderId), 'detail'] as const;
export const ordersPageKey = (search: OrdersSearch) => qk.orders(search.queue, toPageQuery(search));

/**
 * The whole screen, one request, then one more per hundred rows the operator asks for.
 *
 * Infinite rather than a single page because Delivered is over a thousand orders and All is
 * nearly two thousand: a screen that stops at a hundred with a sentence explaining why is a
 * screen the owner leaves for the legacy tab. The tiles, the queue counts and the unmatched
 * payments are read from the first page only, because they are totals rather than rows and
 * every page repeats the same ones.
 *
 * `keepPreviousData` is what makes the search feel instant: the previous rows stay on
 * screen, dimmed by the caller, while the next query resolves, so typing a name does not
 * flash an empty table between keystrokes.
 */
export interface OrdersPageCursor {
  cursor: string;
  /**
   * The range page one was actually served under.
   *
   * Auto-widen happens on the server and only on the first page, so a cursor minted after a
   * widen points into all-time rows. Asking for page two under the operator's original 7d
   * would filter every one of them out and return nothing, while `hasNextPage` said there
   * was more. The page carries its own range forward.
   */
  range: OrderRange;
}

export function useOrdersPage(search: OrdersSearch) {
  return useInfiniteQuery<
    OrdersPageResponseView,
    unknown,
    { pages: OrdersPageResponseView[] },
    readonly unknown[],
    OrdersPageCursor | null
  >({
    queryKey: ordersPageKey(search),
    queryFn: ({ pageParam }) =>
      ordersApi.page({
        ...toPageQuery(search),
        ...(pageParam ? { cursor: pageParam.cursor, range: pageParam.range } : {}),
      }),
    initialPageParam: null,
    getNextPageParam: (last, _all, lastParam) =>
      last.next_cursor
        ? { cursor: last.next_cursor, range: last.widened?.to ?? lastParam?.range ?? search.range }
        : null,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

/** The drawer. Fetched only while one is open. */
export function useOrderDrawer(orderId: number | null) {
  return useQuery<OrderDrawerDetailView>({
    queryKey: orderDetailKey(orderId ?? 0),
    queryFn: () => ordersApi.detail(orderId as number),
    enabled: orderId !== null,
    staleTime: 10_000,
  });
}

/**
 * The definitions behind the hover popovers. They change when the owner settles a pending
 * default, not every minute, so this one is cached hard and never polled.
 */
export function useOrdersLegend() {
  return useQuery({
    queryKey: ordersLegendKey,
    queryFn: () => ordersApi.legend(),
    staleTime: 600_000,
  });
}

/**
 * Everything one order's write can have changed.
 *
 * The drawer, the page and the rail counts, in one place, because a mark paid that leaves
 * the count in the rail saying the order is still awaiting payment is worse than no count.
 */
export function useInvalidateOrder() {
  const client = useQueryClient();
  // Stable across renders, or every useCallback and useMemo that lists it downstream is
  // rebuilt on every render and the memoisation is decorative.
  return useCallback(
    (orderId?: number) => {
      if (orderId !== undefined) {
        void client.invalidateQueries({ queryKey: ['order', String(orderId)] });
      }
      void client.invalidateQueries({ queryKey: ['orders'] });
    },
    [client],
  );
}

export interface OrderWriteOptions<TResult> {
  onSuccess?: (result: TResult) => void;
  onError?: (error: unknown) => void;
}

/**
 * One write, then a refetch of what it touched.
 *
 * Every action in the drawer and on a row goes through this: it waits for the server, it
 * refetches rather than patching the cache by hand, and it hands the caller the server's
 * own result so the screen renders what happened rather than what was asked for.
 */
export function useOrderWrite<TVariables, TResult>(
  run: (variables: TVariables) => Promise<TResult>,
  orderId: number | null,
  options?: OrderWriteOptions<TResult>,
): UseMutationResult<TResult, unknown, TVariables> {
  const invalidate = useInvalidateOrder();
  return useMutation<TResult, unknown, TVariables>({
    mutationFn: run,
    onSuccess: (result) => {
      invalidate(orderId ?? undefined);
      options?.onSuccess?.(result);
    },
    ...(options?.onError ? { onError: options.onError } : {}),
  });
}
